/**
 * Utility functions for parsing and formatting event values
 * Centralizes event value format handling to avoid inconsistencies
 */

export interface ParsedEventValue {
  eventId: number;
  eventName: string;
  pixelId?: string;
}

/**
 * Parse an event value string into its components
 * Supports formats:
 * - "eventId-eventName"
 * - "eventId-eventName-pixelId"
 *
 * @param eventValue - The event value string to parse
 * @returns Parsed components or null if invalid
 */
export const parseEventValue = (eventValue: string): ParsedEventValue | null => {
  if (!eventValue || typeof eventValue !== 'string') {
    return null;
  }

  const parts = eventValue.split('-');
  if (parts.length < 2) {
    return null;
  }

  const eventIdStr = parts[0];
  const eventId = parseInt(eventIdStr, 10);

  if (isNaN(eventId)) {
    return null;
  }

  // Check if last part looks like a pixel ID (long alphanumeric string)
  // Pixel IDs are typically 20+ characters, event names with single words are shorter
  const lastPart = parts[parts.length - 1];
  const hasPixelId = parts.length >= 3 && lastPart.length > 10;

  let pixelId: string | undefined;
  let eventNameParts: string[];

  if (hasPixelId) {
    pixelId = lastPart;
    eventNameParts = parts.slice(1, -1);
  } else {
    eventNameParts = parts.slice(1);
  }

  const eventName = eventNameParts.join('-');

  if (!eventName) {
    return null;
  }

  return { eventId, eventName, pixelId };
};

/**
 * Format an event value from its components
 *
 * @param eventId - The event ID
 * @param eventName - The event name
 * @param pixelId - Optional pixel ID
 * @returns Formatted event value string
 */
export const formatEventValue = (eventId: number, eventName: string, pixelId?: string): string => {
  const base = `${eventId}-${eventName}`;
  return pixelId ? `${base}-${pixelId}` : base;
};

/**
 * Generate a combination key for tracking event-pixel combinations
 *
 * @param eventId - The event ID
 * @param pixelId - Optional pixel ID
 * @returns Combination key string
 */
export const generateCombinationKey = (eventId: number, pixelId?: string): string => {
  return pixelId ? `event-${eventId}-${pixelId}` : `event-${eventId}`;
};

/**
 * Check if two event values represent the same event (ignoring pixel)
 *
 * @param eventValue1 - First event value
 * @param eventValue2 - Second event value
 * @returns True if they represent the same event
 */
export const isSameEvent = (eventValue1: string, eventValue2: string): boolean => {
  const parsed1 = parseEventValue(eventValue1);
  const parsed2 = parseEventValue(eventValue2);

  if (!parsed1 || !parsed2) {
    return false;
  }

  return parsed1.eventId === parsed2.eventId && parsed1.eventName === parsed2.eventName;
};

/**
 * Check if an event value matches a specific event ID and name
 *
 * @param eventValue - The event value to check
 * @param eventId - The event ID to match
 * @param eventName - The event name to match
 * @returns True if matches
 */
export const matchesEvent = (eventValue: string, eventId: number, eventName: string): boolean => {
  const parsed = parseEventValue(eventValue);
  if (!parsed) {
    return false;
  }

  return parsed.eventId === eventId && parsed.eventName === eventName;
};

/**
 * Extract all unique event IDs from an array of event values
 *
 * @param eventValues - Array of event value strings
 * @returns Array of unique event IDs
 */
export const extractEventIds = (eventValues: string[]): number[] => {
  const eventIds = new Set<number>();

  eventValues.forEach(eventValue => {
    const parsed = parseEventValue(eventValue);
    if (parsed) {
      eventIds.add(parsed.eventId);
    }
  });

  return Array.from(eventIds);
};

/**
 * Extract all unique pixel IDs from an array of event values
 *
 * @param eventValues - Array of event value strings
 * @returns Array of unique pixel IDs (excluding undefined)
 */
export const extractPixelIds = (eventValues: string[]): string[] => {
  const pixelIds = new Set<string>();

  eventValues.forEach(eventValue => {
    const parsed = parseEventValue(eventValue);
    if (parsed?.pixelId) {
      pixelIds.add(parsed.pixelId);
    }
  });

  return Array.from(pixelIds);
};
